import { RedoFilterDropdownAnchor } from "@redotech/arbiter/filter-group/redo-filter-group";
import { RedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoListDropdown } from "@redotech/arbiter/list/redo-list-dropdown";
import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useEffect, useMemo, useState } from "react";

export const SimpleFilterDropdown = genericMemo(function SimleFilterDropdown<
  T extends string,
>({
  value,
  setValue,
  options,
  optionToFriendlyName,
  optionToSupportingText,
  openOnRender,
  filterStyle,
  toggle = false,
}: {
  value: T | null | undefined;
  setValue(query: T): void;
  options: readonly T[];
  optionToFriendlyName: (option: T) => string;
  /** Second line under each option, for values whose names need explaining. */
  optionToSupportingText?: (option: T) => string | undefined;
  openOnRender?: boolean;
  filterStyle: "query" | "value";
  toggle?: boolean;
}) {
  const optionItems: RedoListItem<T>[] = useMemo(() => {
    return options.map((option) => ({
      id: `${option}`,
      value: option,
      type: "text",
      text: optionToFriendlyName(option),
      supportingText: optionToSupportingText?.(option),
    }));
  }, [options, optionToFriendlyName, optionToSupportingText]);

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const isToggle = toggle && options.length === 2;

  useEffect(() => {
    if (openOnRender && dropdownButtonRef && !isToggle) {
      dropdownButtonRef.click();
    }
  }, [openOnRender, dropdownButtonRef, isToggle]);

  const selectOption = useHandler(({ item }: { item: RedoListItem<T> }) =>
    setValue(item.value),
  );

  if (isToggle) {
    const other = options.find((option) => option !== value) ?? options[0];
    return (
      <RedoFilterDropdownAnchor
        color={filterStyle === "query" ? "secondary" : "primary"}
        onClick={() => other !== undefined && setValue(other)}
        text={value ? optionToFriendlyName(value) : "..."}
        weight={filterStyle === "query" ? "regular" : "semibold"}
      />
    );
  }

  return (
    <>
      <RedoFilterDropdownAnchor
        color={filterStyle === "query" ? "secondary" : "primary"}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        ref={setDropdownButtonRef}
        text={value ? optionToFriendlyName(value) : "..."}
        weight={filterStyle === "query" ? "regular" : "semibold"}
      />
      <RedoListDropdown<T>
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor={false}
        items={optionItems}
        itemSelected={selectOption}
        selectedItems={value || undefined}
        setDropdownOpen={setDropdownOpen}
        size="xs"
      />
    </>
  );
});
